import { z } from "zod"

import type { ColumnDataType } from "../components/table-columns"
import type { TFunction } from "i18next"

export type OptionItem = {
  id: string
  label: string
  color: string
}

export type OptionType = {
  id: string
  name: string
  options: OptionItem[]
  type: "personal" | "company"
  owner_id: string
  created_at: string
}

export const DEFAULT_OPTION_COLORS = [
  "#EF4444", // red
  "#F97316", // orange
  "#F59E0B", // amber
  "#EAB308", // yellow
  "#84CC16", // lime
  "#22C55E", // green
  "#10B981", // emerald
  "#14B8A6", // teal
  "#06B6D4", // cyan
  "#0EA5E9", // sky
  "#3B82F6", // blue
  "#6366F1", // indigo
  "#8B5CF6", // violet
  "#A855F7", // purple
  "#D946EF", // fuchsia
  "#EC4899", // pink
  "#F43F5E", // rose
  "#78716C", // stone
  "#6B7280", // gray
  "#71717A", // zinc
  "#DC2626", // red-600
  "#EA580C", // orange-600
  "#CA8A04", // yellow-600
  "#16A34A", // green-600
  "#0D9488", // teal-600
  "#0284C7", // sky-600
  "#2563EB", // blue-600
  "#7C3AED", // violet-600
  "#C026D3", // fuchsia-600
  "#DB2777", // pink-600
] as const

// User-friendly data type configuration
export const DATA_TYPE_CONFIG = {
  text: {
    value: "string",
    label: "Text",
    description: "Names, descriptions, addresses, or any text content",
  },
  number: {
    value: "number",
    label: "Number",
    description: "Numeric values, quantities, or measurements",
  },
  yesno: {
    value: "boolean",
    label: "Yes/No",
    description: "True/false values, status indicators, or checkboxes",
  },
  options: {
    value: "options",
    label: "Custom",
    description: "Custom categories for classification",
  },
} as const

export const DATA_TYPES: readonly ColumnDataType[] = [
  "string",
  "number",
  "boolean",
  "options",
] as const

// Helper function to get display label
export function getDataTypeLabel(value: ColumnDataType, t?: TFunction): string {
  if (t) {
    return t(`pages.ai_parser.add_columns_dialog.data_types.${value}`)
  }
  const config = Object.values(DATA_TYPE_CONFIG).find((type) => type.value === value)
  return config?.label || value
}

// Helper function to get description
export function getDataTypeDescription(value: ColumnDataType, t?: TFunction): string {
  if (t) {
    return t(`pages.ai_parser.add_columns_dialog.data_type_descriptions.${value}`)
  }
  const config = Object.values(DATA_TYPE_CONFIG).find((type) => type.value === value)
  return config?.description || ""
}
export type DataType = (typeof DATA_TYPES)[number]

export const optionItemSchema = z.object({
  id: z.string(),
  label: z.string().min(1),
  color: z.string(),
})

export const createColumnSchema = (t?: TFunction) =>
  z.object({
    name: z.string().min(1, t?.("pages.ai_parser.validation.name_required") || "Name is required"),
    description: z
      .string()
      .min(1, t?.("pages.ai_parser.validation.description_required") || "Description is required"),
    dataType: z.enum(DATA_TYPES as [ColumnDataType, ...ColumnDataType[]]),
    optionTypeId: z.string().optional(),
    options: z.array(optionItemSchema).default([]),
  })

export const columnSchema = createColumnSchema()

export type FormColumn = z.input<typeof columnSchema>

export const createAddColumnsSchema = (t?: TFunction) =>
  z.object({
    columns: z
      .array(createColumnSchema(t))
      .min(
        1,
        t?.("pages.ai_parser.validation.at_least_one_column") || "At least one column is required"
      ),
  })

export const addColumnsSchema = createAddColumnsSchema()

export type AddColumnsFormData = z.input<typeof addColumnsSchema>
